/**
 * Module dependencies.
 */

import { ReactNode, useEffect, useState } from 'react';
import { color } from 'styles/utils/colors';
import { css } from '@emotion/react';
import { ifProp } from 'styled-tools';
import styled from '@emotion/styled';

/**
 * `Options` type.
 */

type Options = {
  closeMenu: () => void;
  isOpen: boolean;
  renderContent: boolean;
  showContent: boolean;
};

/**
 * `AnimatedOverlayProps` type.
 */

type AnimatedOverlayProps = {
  children: (options: Options) => ReactNode;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
};

/**
 * `OverlayContainer` styled component.
 */

const OverlayContainer = styled.div<{
  isOpen: boolean;
}>`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 999;
  overflow: hidden;
  pointer-events: none;

  ${ifProp(
    'isOpen',
    css`
      pointer-events: all;
    `,
  )}
`;

/**
 * Animate position.
 */

const animatePosition = 50;

/**
 * `Layer` styled component.
 */

const Layer = styled.div<{ isOpen: boolean }>`
  position: absolute;
  width: 0;
  height: 0;
  background-color: ${color('primary800', 0.55)};
  opacity: 1;
  transition: all 0.8s ease-in-out;

  &.top-left {
    left: -${animatePosition}vw;
    top: -${animatePosition}vh;
    border-bottom-right-radius: 100%;
  }

  &.top-right {
    right: -${animatePosition}vw;
    top: -${animatePosition}vh;
    border-bottom-left-radius: 100%;
  }

  &.bottom-left {
    left: -${animatePosition}vw;
    bottom: -${animatePosition}vh;
    border-top-right-radius: 100%;
  }

  &.bottom-right {
    right: -${animatePosition}vw;
    bottom: -${animatePosition}vh;
    border-top-left-radius: 100%;
  }

  ${ifProp(
    'isOpen',
    css`
      width: 220vw;
      height: 220vh;
    `,
  )}
`;

/**
 * Export `Content` styled component.
 */

export const Content = styled.div<{
  isOpen: boolean;
}>`
  min-height: 100vh;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  display: flex;
  transition: opacity 0.35s ease-in-out;

  ${ifProp(
    'isOpen',
    css`
      opacity: 1;
    `,
    css`
      opacity: 0;
      pointer-events: none;
    `,
  )}
`;

/**
 * Constants.
 */

const ANIMATION_DURATION = 150;
const CONTENT_RENDER_DELAY = 100;

/**
 * Export `AnimatedBackground` component.
 */

export function AnimatedBackground({
  children,
  isOpen,
  setIsOpen,
}: AnimatedOverlayProps) {
  const [state, setState] = useState({
    menuOpen: false,
    renderContent: false,
    showContent: false,
  });

  useEffect(() => {
    let showContentTimeout;
    let renderContentTimeout;
    let menuCloseTimeout;

    if (isOpen) {
      renderContentTimeout = setTimeout(() => {
        setState((prev) => ({ ...prev, renderContent: true }));
      }, ANIMATION_DURATION - CONTENT_RENDER_DELAY);

      setState({ menuOpen: true, renderContent: false, showContent: false });

      showContentTimeout = setTimeout(() => {
        setState({ menuOpen: true, renderContent: true, showContent: true });
      }, ANIMATION_DURATION);
    } else {
      setState((prev) => ({ ...prev, showContent: false }));

      renderContentTimeout = setTimeout(() => {
        setState((prev) => ({ ...prev, renderContent: false }));
      }, ANIMATION_DURATION + CONTENT_RENDER_DELAY);

      menuCloseTimeout = setTimeout(() => {
        setState({ menuOpen: false, renderContent: false, showContent: false });
      }, ANIMATION_DURATION);
    }

    return () => {
      clearTimeout(showContentTimeout);
      clearTimeout(renderContentTimeout);
      clearTimeout(menuCloseTimeout);
    };
  }, [isOpen]);

  return (
    <OverlayContainer isOpen={state.menuOpen}>
      <Layer
        className={'top-left'}
        isOpen={state.menuOpen}
        style={{ transitionDelay: '0' }}
      />

      <Layer
        className={'top-right'}
        isOpen={state.menuOpen}
        style={{ transitionDelay: '80ms' }}
      />

      <Layer
        className={'bottom-left'}
        isOpen={state.menuOpen}
        style={{ transitionDelay: '200ms' }}
      />

      <Layer
        className={'bottom-right'}
        isOpen={state.menuOpen}
        style={{ transitionDelay: '250ms' }}
      />

      {state.renderContent && (
        <Content isOpen={state.showContent}>
          {children({
            closeMenu: () => setIsOpen(false),
            isOpen: state.menuOpen,
            renderContent: state.renderContent,
            showContent: state.showContent,
          })}
        </Content>
      )}
    </OverlayContainer>
  );
}
